import { z } from "zod";
import { MAX_TEXT_SHORT } from "../constants.js";

// 業種マスタ（基本設計 §2.2/§2.4/§4.1）。業種は自由入力せずここから1つ選ぶ。

// code は識別子・FK参照先なので作成後不変。^[a-z][a-z0-9_]{1,30}$（§2.2）。
// 予約語: ルートの静的パス（/api/industries/order）と衝突する code を禁じる。
// これを許すと `PATCH /industries/order` が reorder に吸われ、その業種を PATCH で改称/廃止できなくなる。
const RESERVED_INDUSTRY_CODES = ["order"];
export const industryCodeSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z][a-z0-9_]{1,30}$/,
    "業種コードは英小文字で始まり、英小文字・数字・_（2〜31文字）で指定してください",
  )
  .refine((c) => !RESERVED_INDUSTRY_CODES.includes(c), {
    message: "このコードは予約語のため使用できません。",
  });

const industryLabelSchema = z.string().trim().min(1, "表示名は必須です").max(MAX_TEXT_SHORT);
const sortOrderSchema = z.coerce.number().int().min(0).max(100000);

// ── 選択肢用の出力（GET /api/industries・guest可）──
// 件数が14程度でも非活性は配らない（表示は企業APIの industryLabel から引く・§4.1）。
export const industryOptionSchema = z.object({
  code: z.string(),
  label: z.string(),
});
export type IndustryOption = z.infer<typeof industryOptionSchema>;

// ── 保守用の出力（GET /api/industries?includeInactive=true・admin）──
// 選択肢用（{code,label}）とは別物。並べ替え・廃止は現在の並び順・活性・影響企業数が要る（§4.1）。
export const industryAdminSchema = z.object({
  code: z.string(),
  label: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  companyCount: z.number().int(),
});
export type IndustryAdmin = z.infer<typeof industryAdminSchema>;

// ── 一覧クエリ ──
// includeInactive=true は admin のみ（非admin が付けたら route で 400・§4.1）。
// 省略・"false" は false。"true"/"false" 以外は 400（黙って無視しない）。
export const industryListQuerySchema = z.object({
  includeInactive: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});
export type IndustryListQuery = z.infer<typeof industryListQuerySchema>;

// ── 追加（POST /api/industries）──
// strict: 未知キー（isActive 等）を黙って無視せず 400（update と方針を揃える・S-3）。
export const industryCreateInputSchema = z
  .object({
    code: industryCodeSchema,
    label: industryLabelSchema,
    sortOrder: sortOrderSchema,
  })
  .strict();
export type IndustryCreateInput = z.infer<typeof industryCreateInputSchema>;

// ── 部分更新（PATCH /api/industries/:code）──
// 未指定は変更しない。body に code を含めたら 400（不変・§4.1）＝ strict で未知キーを弾く。
export const industryUpdateInputSchema = z
  .object({
    label: industryLabelSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type IndustryUpdateInput = z.infer<typeof industryUpdateInputSchema>;

// ── 一括並べ替え（PATCH /api/industries/order）──
// 1トランザクションで行う（1件ずつだと途中失敗で順序が壊れる・§4.1）。
// 上限を設ける（業種数は現実的に数十件・巨大配列での長時間トランザクション防止・S-2）。
export const industryReorderInputSchema = z
  .array(z.object({ code: z.string(), sortOrder: sortOrderSchema }).strict())
  .min(1, "並べ替え対象がありません")
  .max(1000, "並べ替え対象が多すぎます");
export type IndustryReorderInput = z.infer<typeof industryReorderInputSchema>;
